import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Facebook, Youtube, Linkedin } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-slate-900 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/sgfn7pb6_NASSAU%20LOGO.fw.png"
                alt="Nassau Engineering Ltd"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Leaders in steel fabrication and engineering consultancy. Delivering precision, 
              quality, and reliability across Ireland.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/NassauEngineeringLtd/" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="text-slate-400 hover:text-red-400 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@nassauengineering" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="text-slate-400 hover:text-red-400 transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a 
                href="https://ie.linkedin.com/company/nassau-engineering-ltd" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="text-slate-400 hover:text-red-400 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              <span className="text-red-500">Quick</span> Links
            </h3>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Services", path: "/services" },
                { name: "Projects", path: "/projects" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              <span className="text-red-500">Our</span> Services
            </h3>
            <ul className="space-y-3">
              {[
                "Sheet Metal Fabrication",
                "Laser Cutting",
                "BIM Services",
                "Pharmaceutical Clean Rooms",
              ].map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-slate-400 hover:text-red-400 transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              <span className="text-red-500">Contact</span> Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  Unit 4b, Canal Bank, Hume Ave,<br />
                  Park West Industrial Park,<br />
                  Dublin, D12 ARX3, Ireland
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <a 
                  href="tel:+35316301410"
                  data-testid="footer-phone"
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                >
                  +353 1 630 1410
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <a 
                  href="mailto:info@nassauengineering.ie"
                  data-testid="footer-email"
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                >
                  info@nassauengineering.ie
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="text-slate-400 text-sm">
                  <p>Mon - Thurs: 8:00 AM - 4:30 PM</p>
                  <p>Fri: 8:00 AM - 3:30 PM</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-500 text-sm">
              © {currentYear} Nassau Engineering Ltd. All rights reserved.
            </p>
            <div className="flex items-center space-x-4">
              <Link to="/privacy-policy" className="text-slate-500 hover:text-blue-400 text-sm transition-colors">
                Privacy Policy
              </Link>
              <span className="text-slate-700">|</span>
              <Link to="/cookie-policy" className="text-slate-500 hover:text-blue-400 text-sm transition-colors">
                Cookie Policy
              </Link>
              <span className="text-slate-700">|</span>
              <Link to="/terms" className="text-slate-500 hover:text-blue-400 text-sm transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
